/**
 * 封面圖上傳前處理(R245;BE docs/92 §4「建議但非必要」)
 * -----------------------------------------------------------------------------
 * 上傳前在 canvas 把長邊縮到 1600px。
 *
 * **為什麼值得做**:文件說這只是「省使用者的上傳時間」,但實際效益更大一層 ——
 * BE 的閘門是 **≤5 MB**,而手機直出照片動輒 4–12 MB。不縮就是一大票使用者選了
 * 自己的照片之後直接吃 `COVER_SIZE_EXCEEDS_LIMIT`,而他們手上沒有裁圖工具。
 * 縮到 1600px 之後典型照片是 200–400 KB,那類失敗整個消失。順帶也繞開
 * `COVER_DIMENSIONS_EXCEED_LIMIT`(縮完約 2–3 MP,遠低於 PNG/WebP 的 12 MP 上限)。
 *
 * ⚠️ **全程 fail-soft — 任何一步不成就回原檔**。這條路只能幫忙、不能擋路:
 * jsdom 沒有 `createImageBitmap`、舊瀏覽器沒有 canvas WebP、記憶體吃緊 decode 失敗
 * ……全部退回「原封不動送出去」,由後端的閘門去判。所以最壞情況等於沒做這件事。
 *
 * ⚠️ **EXIF 方向**:`imageOrientation: 'from-image'` 一定要帶。手機直拍的直式照片
 * 是「橫著存 + EXIF 標記要轉 90 度」,canvas 重新編碼會把 EXIF 丟掉 —— 沒帶這個
 * 選項就會把使用者的照片轉倒。這是「重新編碼」這件事本身帶進來的風險,不是縮圖
 * 帶進來的。
 */

/** BE `COVER_MAX_UPLOAD_BYTES`(5 MB)鏡射 —— 用來在送出前先給出誠實的錯誤。 */
export const COVER_MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

/** BE magic-byte 白名單鏡射;`<input accept>` 與送出前檢查共用。 */
export const COVER_ACCEPT_MIME = ['image/png', 'image/jpeg', 'image/webp'] as const;

/** 客戶端縮圖目標長邊。BE 最終只留 480,這裡留餘裕給日後改版。 */
export const COVER_CLIENT_MAX_EDGE = 1600;

/** WebP 編碼品質;1600px 下 0.85 肉眼無差,檔案約為 0.95 的一半。 */
const WEBP_QUALITY = 0.85;

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), type, quality);
  });
}

/** `photo.HEIC.jpg` → `photo.HEIC.webp`;沒有副檔名就補一個。 */
function withExtension(name: string, ext: string): string {
  const dot = name.lastIndexOf('.');
  const stem = dot > 0 ? name.slice(0, dot) : name;
  return `${stem}.${ext}`;
}

/**
 * 縮到長邊 `COVER_CLIENT_MAX_EDGE` 以內;做不到就原檔奉還。
 *
 * 兩種情況**刻意不換**(直接回原檔):
 *   1. 本來就夠小 —— 重新編碼只會多一次失真,沒有好處
 *   2. 縮完反而更大 —— `toBlob('image/webp')` 在不支援 WebP 的瀏覽器會**靜默**
 *      退回 PNG,而照片轉 PNG 常常比原始 JPEG 大好幾倍。用結果的大小當判準,
 *      不用「瀏覽器宣稱支援什麼」當判準。
 */
export async function prepareCoverUpload(file: File): Promise<File> {
  try {
    if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') return file;

    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    try {
      const longest = Math.max(bitmap.width, bitmap.height);
      if (longest <= COVER_CLIENT_MAX_EDGE) return file;

      const scale = COVER_CLIENT_MAX_EDGE / longest;
      const w = Math.max(1, Math.round(bitmap.width * scale));
      const h = Math.max(1, Math.round(bitmap.height * scale));

      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return file;
      ctx.drawImage(bitmap, 0, 0, w, h);

      const blob = await canvasToBlob(canvas, 'image/webp', WEBP_QUALITY);
      if (!blob || blob.size >= file.size) return file;

      // blob.type 可能是 image/webp 或(靜默退回時)image/png — 兩者都在 BE 白名單內。
      const ext = blob.type === 'image/png' ? 'png' : 'webp';
      return new File([blob], withExtension(file.name, ext), { type: blob.type });
    } finally {
      bitmap.close?.();
    }
  } catch {
    // decode / encode / OOM 全部走這裡 —— 原檔送出,交給後端閘門判。
    return file;
  }
}
